/**
 * ARSPURA order-level ad attribution
 * Spec: ARSPURA_订单级广告归因_D1_字段对照表.md
 * D2: URL parse + landing snapshot
 * D3: first-party Cookie + localStorage (90d)
 * D4: First / Last Non-Direct / Conversion rules
 *     — first/last + click IDs persist; conv_* computed on demand (not stored)
 * D6: on add-to-cart, merge-write Cart attributes via cart/update.js
 * D7: cart page + checkout buttons — sync again before leaving for checkout
 * D8: Shop Pay / accelerated checkout / Buy Now — attrs must be on cart before leave
 * D13: when syncing conv, clear absent _attr_conv_* keys ("" removes cart attrs)
 *      — fixes Direct checkout leaving stale conv UTM / click_id from earlier touches
 */
(function () {
  'use strict';

  var SCHEMA_VERSION = '1';
  var MAX_LEN = 255;
  var DEBUG = true;
  var COOKIE_NAME = 'arspura_attr';
  var LS_KEY = 'arspura_attr_v1';
  var TTL_SEC = 90 * 24 * 60 * 60;
  var syncInFlight = null;
  /** All conv cart keys; absent ones are sent as "" so Shopify drops stale values. */
  var CONV_ATTR_KEYS = [
    '_attr_conv_channel',
    '_attr_conv_utm_source',
    '_attr_conv_utm_medium',
    '_attr_conv_utm_campaign',
    '_attr_conv_utm_content',
    '_attr_conv_utm_term',
    '_attr_conv_click_id',
    '_attr_conv_click_id_type',
    '_attr_conv_landing',
    '_attr_conv_referrer',
    '_attr_conv_ts',
  ];
  var ACCEL_SELECTOR = [
    '.additional-checkout-buttons',
    '.cart__dynamic-checkout-buttons',
    '.shopify-payment-button',
    'shopify-accelerated-checkout',
    'shopify-accelerated-checkout-cart',
    '[data-shopify="payment-button"]',
    '[data-shopify-buttoncontainer]',
  ].join(',');

  var UTM_KEYS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
  ];
  var CLICK_ID_KEYS = ['gclid', 'fbclid', 'msclkid'];

  function truncate(str) {
    if (str == null || str === '') return '';
    str = String(str);
    return str.length > MAX_LEN ? str.slice(0, MAX_LEN) : str;
  }

  function nowUtcIso() {
    return new Date().toISOString().replace(/\.\d{3}Z$/, 'Z');
  }

  function getParam(params, key) {
    var v = params.get(key);
    if (v == null || v === '') return '';
    return truncate(v);
  }

  function landingFromLocation(loc) {
    return truncate(loc.pathname + loc.search);
  }

  function externalReferrer(ref, loc) {
    if (!ref) return '';
    try {
      var u = new URL(ref);
      if (u.hostname === loc.hostname) return '';
      return truncate(ref);
    } catch (e) {
      return truncate(ref);
    }
  }

  function isQualifying(touch) {
    var i;
    for (i = 0; i < UTM_KEYS.length; i++) {
      if (touch[UTM_KEYS[i]]) return true;
    }
    for (i = 0; i < CLICK_ID_KEYS.length; i++) {
      if (touch[CLICK_ID_KEYS[i]]) return true;
    }
    return false;
  }

  function pickClickId(touch) {
    if (touch.gclid) return { id: touch.gclid, type: 'gclid' };
    if (touch.fbclid) return { id: touch.fbclid, type: 'fbclid' };
    if (touch.msclkid) return { id: touch.msclkid, type: 'msclkid' };
    return { id: '', type: '' };
  }

  function normalizeToken(s) {
    return String(s || '')
      .toLowerCase()
      .replace(/\s+/g, '');
  }

  function resolveChannel(touch) {
    if (!isQualifying(touch)) return 'direct';

    if (touch.gclid) return 'google';
    if (touch.fbclid) return 'meta';
    if (touch.msclkid) return 'bing';

    var source = normalizeToken(touch.utm_source);
    var medium = normalizeToken(touch.utm_medium);

    var metaSources = {
      facebook: 1,
      fb: 1,
      ig: 1,
      instagram: 1,
      insta: 1,
      meta: 1,
      an: 1,
    };
    var googleSources = {
      google: 1,
      googleads: 1,
      adwords: 1,
      youtube: 1,
      gdn: 1,
    };
    var bingSources = {
      bing: 1,
      microsoft: 1,
      msn: 1,
      bingads: 1,
    };
    var emailSources = {
      email: 1,
      klaviyo: 1,
      omnisend: 1,
      newsletter: 1,
      mailchimp: 1,
      sms: 1,
    };

    if (metaSources[source]) return 'meta';
    if (googleSources[source]) return 'google';
    if (bingSources[source]) return 'bing';
    if (emailSources[source]) return 'email';
    if (medium === 'email' || medium === 'e-mail') return 'email';

    return 'other';
  }

  function parseCurrentTouch(loc, referrer) {
    var params = new URLSearchParams(loc.search);
    var touch = {
      utm_source: getParam(params, 'utm_source'),
      utm_medium: getParam(params, 'utm_medium'),
      utm_campaign: getParam(params, 'utm_campaign'),
      utm_content: getParam(params, 'utm_content'),
      utm_term: getParam(params, 'utm_term'),
      fbclid: getParam(params, 'fbclid'),
      gclid: getParam(params, 'gclid'),
      msclkid: getParam(params, 'msclkid'),
      landing: landingFromLocation(loc),
      referrer: externalReferrer(referrer, loc),
      ts: nowUtcIso(),
    };

    var click = pickClickId(touch);
    touch.click_id = click.id;
    touch.click_id_type = click.type;
    touch.qualifying = isQualifying(touch);
    touch.channel = resolveChannel(touch);
    touch.schema_version = SCHEMA_VERSION;

    return touch;
  }

  function touchToStoredGroup(touch) {
    return {
      channel: touch.channel,
      utm_source: touch.utm_source,
      utm_medium: touch.utm_medium,
      utm_campaign: touch.utm_campaign,
      utm_content: touch.utm_content,
      utm_term: touch.utm_term,
      click_id: touch.click_id,
      click_id_type: touch.click_id_type,
      landing: touch.landing,
      referrer: touch.referrer,
      ts: touch.ts,
    };
  }

  /**
   * Conversion snapshot — D4 rule:
   * - Computed on demand (checkout / cart write), NOT persisted in Cookie
   * - Qualifying hit → conv_* = current touch
   * - Direct hit → conv_channel=direct, UTM/click empty; does not change last
   */
  function buildConversion(touch) {
    if (!touch || !touch.qualifying) {
      return {
        channel: 'direct',
        utm_source: '',
        utm_medium: '',
        utm_campaign: '',
        utm_content: '',
        utm_term: '',
        click_id: '',
        click_id_type: '',
        landing: touch && touch.landing ? touch.landing : '',
        referrer: touch && touch.referrer ? touch.referrer : '',
        ts: touch && touch.ts ? touch.ts : nowUtcIso(),
      };
    }
    return touchToStoredGroup(touch);
  }

  function formatSummary(group, isConversion) {
    if (!group) return '(none)';
    var channel = group.channel || 'direct';
    var source = group.utm_source || '(none)';
    var campaign = group.utm_campaign || '(none)';
    var parts = [channel, source, campaign];
    if (!(isConversion && channel === 'direct')) {
      if (group.click_id_type && group.click_id) {
        parts.push(group.click_id_type + '=' + String(group.click_id).slice(0, 8) + '…');
      }
    }
    return parts.join(' | ');
  }

  function emptyState() {
    return {
      schema_version: SCHEMA_VERSION,
      expires_at: nowUtcIso(),
      first: null,
      last: null,
      fbclid: '',
      gclid: '',
      msclkid: '',
      updated_at: nowUtcIso(),
    };
  }

  function isExpired(state) {
    if (!state || !state.expires_at) return true;
    try {
      return Date.now() >= new Date(state.expires_at).getTime();
    } catch (e) {
      return true;
    }
  }

  function refreshExpiry(state) {
    state.expires_at = new Date(Date.now() + TTL_SEC * 1000)
      .toISOString()
      .replace(/\.\d{3}Z$/, 'Z');
    state.updated_at = nowUtcIso();
    return state;
  }

  function readCookie() {
    try {
      var parts = document.cookie ? document.cookie.split('; ') : [];
      var i;
      for (i = 0; i < parts.length; i++) {
        var eq = parts[i].indexOf('=');
        if (eq === -1) continue;
        var name = parts[i].slice(0, eq);
        if (name === COOKIE_NAME) {
          return decodeURIComponent(parts[i].slice(eq + 1));
        }
      }
    } catch (e) {
      /* ignore */
    }
    return null;
  }

  function writeCookie(json) {
    try {
      document.cookie =
        COOKIE_NAME +
        '=' +
        encodeURIComponent(json) +
        '; path=/' +
        '; max-age=' +
        TTL_SEC +
        '; SameSite=Lax';
    } catch (e) {
      /* ignore */
    }
  }

  function readLocalStorage() {
    try {
      return window.localStorage.getItem(LS_KEY);
    } catch (e) {
      return null;
    }
  }

  function writeLocalStorage(json) {
    try {
      window.localStorage.setItem(LS_KEY, json);
    } catch (e) {
      /* ignore */
    }
  }

  function parseStored(raw) {
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }

  function readState() {
    var raw = readCookie() || readLocalStorage();
    var state = parseStored(raw);
    if (!state || state.schema_version !== SCHEMA_VERSION || isExpired(state)) {
      return emptyState();
    }
    return state;
  }

  function writeState(state) {
    refreshExpiry(state);
    var json = JSON.stringify(state);
    writeCookie(json);
    writeLocalStorage(json);
    return state;
  }

  /**
   * First / Last Non-Direct rules (D4):
   * - First: write once on first qualifying touch; never overwrite within 90d
   * - Last: update on every qualifying touch; Direct must NOT clear or set last=direct
   * - Platform click IDs: update when present; Direct does not clear them
   */
  function mergeTouchIntoState(state, touch) {
    var i;
    var key;
    var group;

    for (i = 0; i < CLICK_ID_KEYS.length; i++) {
      key = CLICK_ID_KEYS[i];
      if (touch[key]) {
        state[key] = touch[key];
      }
    }

    if (!touch.qualifying) {
      return state;
    }

    group = touchToStoredGroup(touch);

    if (!state.first) {
      state.first = group;
    }

    state.last = group;
    return state;
  }

  function prefixGroup(prefix, group) {
    var out = {};
    if (!group) return out;
    out[prefix + 'channel'] = group.channel || '';
    out[prefix + 'utm_source'] = group.utm_source || '';
    out[prefix + 'utm_medium'] = group.utm_medium || '';
    out[prefix + 'utm_campaign'] = group.utm_campaign || '';
    out[prefix + 'utm_content'] = group.utm_content || '';
    out[prefix + 'utm_term'] = group.utm_term || '';
    out[prefix + 'click_id'] = group.click_id || '';
    out[prefix + 'click_id_type'] = group.click_id_type || '';
    out[prefix + 'landing'] = group.landing || '';
    out[prefix + 'referrer'] = group.referrer || '';
    out[prefix + 'ts'] = group.ts || '';
    return out;
  }

  /**
   * Flat payload for Cart attributes (_attr_* + 3 visible summaries).
   * Empty groups omitted (no blank metafield writes later).
   */
  function getCartPayload(touch, state) {
    var t = touch || parseCurrentTouch(window.location, document.referrer);
    var s = state || readState();
    var conv = buildConversion(t);
    var attrs = {};
    var k;
    var firstFlat;
    var lastFlat;
    var convFlat;

    attrs._attr_schema_version = SCHEMA_VERSION;

    if (s.first) {
      firstFlat = prefixGroup('_attr_first_', s.first);
      for (k in firstFlat) {
        if (Object.prototype.hasOwnProperty.call(firstFlat, k) && firstFlat[k] !== '') {
          attrs[k] = firstFlat[k];
        }
      }
    }

    if (s.last) {
      lastFlat = prefixGroup('_attr_last_', s.last);
      for (k in lastFlat) {
        if (Object.prototype.hasOwnProperty.call(lastFlat, k) && lastFlat[k] !== '') {
          attrs[k] = lastFlat[k];
        }
      }
    }

    convFlat = prefixGroup('_attr_conv_', conv);
    for (k in convFlat) {
      if (Object.prototype.hasOwnProperty.call(convFlat, k) && convFlat[k] !== '') {
        attrs[k] = convFlat[k];
      }
    }
    // D13: merge only overwrites keys we send. Stale conv UTM/click_id from a
    // prior qualifying touch must be deleted when this snapshot omits them
    // (typical: Meta landing → /cart Direct checkout). Shopify removes attrs set to "".
    for (k = 0; k < CONV_ATTR_KEYS.length; k++) {
      if (!Object.prototype.hasOwnProperty.call(attrs, CONV_ATTR_KEYS[k])) {
        attrs[CONV_ATTR_KEYS[k]] = '';
      }
    }

    if (s.fbclid) attrs._attr_fbclid = s.fbclid;
    if (s.gclid) attrs._attr_gclid = s.gclid;
    if (s.msclkid) attrs._attr_msclkid = s.msclkid;

    attrs['First Touch'] = formatSummary(s.first, false);
    attrs['Last Non-Direct'] = formatSummary(s.last, false);
    attrs.Conversion = formatSummary(conv, true);

    return attrs;
  }

  function getSnapshot(touch, state) {
    var t = touch || parseCurrentTouch(window.location, document.referrer);
    var s = state || readState();
    var conv = buildConversion(t);
    return {
      currentTouch: t,
      first: s.first,
      last: s.last,
      conversion: conv,
      fbclid: s.fbclid || '',
      gclid: s.gclid || '',
      msclkid: s.msclkid || '',
      summaries: {
        first: formatSummary(s.first, false),
        last: formatSummary(s.last, false),
        conversion: formatSummary(conv, true),
      },
      cartPreview: getCartPayload(t, s),
    };
  }

  function cartJsUrl() {
    var base =
      window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart';
    return base.indexOf('.js') === -1 ? base + '.js' : base;
  }

  function cartUpdateUrl() {
    return window.routes && window.routes.cart_update_url
      ? window.routes.cart_update_url
      : '/cart/update.js';
  }

  function mergeAttributes(existing, payload) {
    var merged = {};
    var k;
    if (existing) {
      for (k in existing) {
        if (Object.prototype.hasOwnProperty.call(existing, k)) {
          merged[k] = existing[k];
        }
      }
    }
    for (k in payload) {
      if (Object.prototype.hasOwnProperty.call(payload, k)) {
        merged[k] = payload[k];
      }
    }
    return merged;
  }

  /**
   * D6: read cart → merge attribution attributes → cart/update.js
   * Shopify replaces the whole attributes map on update; never send payload alone.
   */
  function syncCartAttributes() {
    if (syncInFlight) return syncInFlight;

    var touch = parseCurrentTouch(window.location, document.referrer);
    var state = mergeTouchIntoState(readState(), touch);
    writeState(state);
    var payload = getCartPayload(touch, state);

    syncInFlight = fetch(cartJsUrl(), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then(function (res) {
        if (!res.ok) throw new Error('cart.js ' + res.status);
        return res.json();
      })
      .then(function (cart) {
        var merged = mergeAttributes(cart.attributes || {}, payload);
        return fetch(cartUpdateUrl(), {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ attributes: merged }),
        }).then(function (res) {
          if (!res.ok) throw new Error('cart/update.js ' + res.status);
          return res.json();
        });
      })
      .then(function (cart) {
        window.ArspuraAttribution.lastCartAttributes = cart.attributes || {};
        window.ArspuraAttribution.currentTouch = touch;
        window.ArspuraAttribution.storedState = state;
        window.ArspuraAttribution.conversion = buildConversion(touch);
        window.ArspuraAttribution.snapshot = getSnapshot(touch, state);
        if (DEBUG && typeof console !== 'undefined' && console.log) {
          console.log(
            '[ArspuraAttribution D8] cart attributes synced',
            cart.attributes
          );
        }
        return cart;
      })
      .catch(function (err) {
        if (DEBUG && typeof console !== 'undefined' && console.warn) {
          console.warn('[ArspuraAttribution D8] sync failed', err);
        }
        return null;
      })
      .then(function (cart) {
        syncInFlight = null;
        return cart;
      });

    return syncInFlight;
  }

  function isCheckoutCartForm(form) {
    if (!form || form.tagName !== 'FORM') return false;
    return (
      form.id === 'cart' ||
      form.id === 'CartDrawer-Form' ||
      form.id === 'cart-notification-form'
    );
  }

  function proceedCheckoutSubmit(form) {
    var btn = form.querySelector('button[name="checkout"]');
    form.setAttribute('data-arspura-checkout-synced', '1');
    if (typeof form.requestSubmit === 'function' && btn) {
      form.requestSubmit(btn);
    } else if (btn) {
      btn.click();
    } else {
      window.location.href =
        (window.routes && window.routes.root ? window.routes.root : '/') + 'checkout';
    }
  }

  /**
   * D7: intercept checkout form submit → sync attributes → continue to checkout.
   * Uses merge (read cart first) so cart note / third-party attrs are not wiped.
   */
  function bindCheckoutSync() {
    document.addEventListener(
      'submit',
      function (e) {
        var form = e.target;
        if (!isCheckoutCartForm(form)) return;

        var submitter = e.submitter;
        if (submitter && submitter.name !== 'checkout') return;

        if (form.getAttribute('data-arspura-checkout-synced') === '1') {
          form.removeAttribute('data-arspura-checkout-synced');
          return;
        }

        e.preventDefault();
        if (typeof e.stopImmediatePropagation === 'function') {
          e.stopImmediatePropagation();
        }

        syncCartAttributes()
          .then(function () {
            proceedCheckoutSubmit(form);
          })
          .catch(function () {
            proceedCheckoutSubmit(form);
          });
      },
      true
    );

    if (DEBUG && typeof console !== 'undefined' && console.log) {
      console.log('[ArspuraAttribution D8] checkout submit hook ready');
    }
  }

  function isCartPage() {
    var path = window.location.pathname || '';
    return path === '/cart' || /\/cart\/?$/.test(path);
  }

  function setAccelButtonsLocked(locked) {
    var nodes = document.querySelectorAll(
      '.additional-checkout-buttons, .cart__dynamic-checkout-buttons, .shopify-payment-button'
    );
    var i;
    for (i = 0; i < nodes.length; i++) {
      if (locked) {
        nodes[i].setAttribute('data-arspura-accel-locked', '1');
        nodes[i].style.pointerEvents = 'none';
      } else {
        nodes[i].removeAttribute('data-arspura-accel-locked');
        nodes[i].style.pointerEvents = '';
      }
    }
  }

  /**
   * D8: Shop Pay / wallets skip the #cart checkout submit path.
   * Attributes must already be on the cart before the wallet UI takes over.
   * - Lock accelerated buttons until the page-load sync finishes (cart page)
   * - pointerdown on wallet / Buy Now areas fires another sync (conversion freshness)
   * Do not preventDefault on wallet clicks (Shopify wallet buttons are fragile to re-click).
   */
  function bindAcceleratedCheckoutSync() {
    document.addEventListener(
      'pointerdown',
      function (e) {
        var el = e.target && e.target.closest ? e.target.closest(ACCEL_SELECTOR) : null;
        if (!el) return;
        if (el.getAttribute('data-arspura-accel-locked') === '1') return;
        syncCartAttributes();
        if (DEBUG && typeof console !== 'undefined' && console.log) {
          console.log('[ArspuraAttribution D8] accelerated checkout → sync kicked');
        }
      },
      true
    );

    if (isCartPage() && typeof MutationObserver === 'function') {
      var observer = new MutationObserver(function () {
        if (document.documentElement.getAttribute('data-arspura-accel-ready') !== '1') {
          setAccelButtonsLocked(true);
        }
      });
      observer.observe(document.documentElement, {
        childList: true,
        subtree: true,
      });
      setTimeout(function () {
        try {
          observer.disconnect();
        } catch (err) {
          /* ignore */
        }
      }, 8000);
    }

    if (DEBUG && typeof console !== 'undefined' && console.log) {
      console.log('[ArspuraAttribution D8] accelerated checkout hook ready');
    }
  }

  function syncOnCartPage() {
    if (!isCartPage()) return null;
    setAccelButtonsLocked(true);
    return syncCartAttributes()
      .then(function (cart) {
        document.documentElement.setAttribute('data-arspura-accel-ready', '1');
        setAccelButtonsLocked(false);
        if (DEBUG && typeof console !== 'undefined' && console.log) {
          console.log('[ArspuraAttribution D8] cart page sync done; wallets unlocked');
        }
        return cart;
      })
      .catch(function (err) {
        document.documentElement.setAttribute('data-arspura-accel-ready', '1');
        setAccelButtonsLocked(false);
        if (DEBUG && typeof console !== 'undefined' && console.warn) {
          console.warn('[ArspuraAttribution D8] cart page sync failed; wallets unlocked', err);
        }
        return null;
      });
  }

  function bindCartSync() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
        if (event && event.source === 'arspura-attribution') return;
        syncCartAttributes();
      });
      if (DEBUG && typeof console !== 'undefined' && console.log) {
        console.log('[ArspuraAttribution D8] subscribed to cartUpdate');
      }
    }
  }

  function run() {
    var touch = parseCurrentTouch(window.location, document.referrer);
    var state = readState();
    state = mergeTouchIntoState(state, touch);
    writeState(state);

    var conversion = buildConversion(touch);
    var snapshot = getSnapshot(touch, state);

    window.ArspuraAttribution = window.ArspuraAttribution || {};
    window.ArspuraAttribution.schemaVersion = SCHEMA_VERSION;
    window.ArspuraAttribution.day = 'D8';
    window.ArspuraAttribution.currentTouch = touch;
    window.ArspuraAttribution.storedState = state;
    window.ArspuraAttribution.conversion = conversion;
    window.ArspuraAttribution.snapshot = snapshot;

    window.ArspuraAttribution.parseCurrentTouch = function () {
      return parseCurrentTouch(window.location, document.referrer);
    };
    window.ArspuraAttribution.getStoredState = function () {
      return readState();
    };
    window.ArspuraAttribution.getConversion = function () {
      return buildConversion(parseCurrentTouch(window.location, document.referrer));
    };
    window.ArspuraAttribution.getSnapshot = function () {
      return getSnapshot();
    };
    window.ArspuraAttribution.getCartPayload = function () {
      return getCartPayload();
    };
    window.ArspuraAttribution.syncCartAttributes = syncCartAttributes;
    window.ArspuraAttribution.refreshStorage = function () {
      var t = parseCurrentTouch(window.location, document.referrer);
      var s = mergeTouchIntoState(readState(), t);
      writeState(s);
      var c = buildConversion(t);
      var snap = getSnapshot(t, s);
      window.ArspuraAttribution.currentTouch = t;
      window.ArspuraAttribution.storedState = s;
      window.ArspuraAttribution.conversion = c;
      window.ArspuraAttribution.snapshot = snap;
      return snap;
    };

    bindCartSync();
    bindCheckoutSync();
    bindAcceleratedCheckoutSync();
    syncOnCartPage();

    if (DEBUG && typeof console !== 'undefined' && console.log) {
      console.log('[ArspuraAttribution D8] currentTouch', touch);
      console.log('[ArspuraAttribution D8] storedState', state);
      console.log('[ArspuraAttribution D8] conversion', conversion);
      console.log('[ArspuraAttribution D8] summaries', snapshot.summaries);
      if (touch.qualifying) {
        console.log(
          '[ArspuraAttribution D8] qualifying → first/last updated | channel=',
          touch.channel,
          '| conv=',
          conversion.channel
        );
      } else {
        console.log(
          '[ArspuraAttribution D8] Direct hit — first/last kept | conv=direct',
          '| first=',
          state.first ? state.first.channel : '(empty)',
          '| last=',
          state.last ? state.last.channel : '(empty)'
        );
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
